import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const NONE = "none";
const BASE_TARGETS = [
  { value: NONE, label: "Do not send" },
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
];

export function PcoFormSync({ formId }) {
  const [connected, setConnected] = useState(false);
  const [opts, setOpts] = useState({ field_definitions: [], workflows: [], note_categories: [] });
  const [fields, setFields] = useState([]);
  const [cfg, setCfg] = useState({ enabled: false, field_map: {}, workflow_id: null, note_category_id: null, phone_location: "Mobile" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: o }, { data: c }] = await Promise.all([
          api.get("/integrations/planning-center/options"),
          api.get(`/integrations/planning-center/forms/${formId}/config`),
        ]);
        setConnected(o.connected);
        setOpts(o);
        setFields(c.fields || []);
        setCfg({ enabled: false, field_map: {}, workflow_id: null, note_category_id: null, phone_location: "Mobile", ...(c.pco_sync || {}) });
      } catch { /* not connected / not found */ }
    })();
  }, [formId]);

  const setTarget = (fid, value) => setCfg((p) => ({ ...p, field_map: { ...p.field_map, [fid]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/integrations/planning-center/forms/${formId}/config`, cfg);
      toast.success("Planning Center settings saved");
    } catch (e) { toast.error("Could not save Planning Center settings"); }
    finally { setSaving(false); }
  };

  if (!connected) {
    return (
      <Card className="p-5 mt-4" data-testid="pco-sync-panel">
        <h3 className="font-semibold text-[#2A2626]">Send to Planning Center</h3>
        <p className="text-sm text-[#5C5856] mt-1">Not connected yet. Connect Planning Center in settings to send responses to your church database.</p>
      </Card>
    );
  }

  const targetOptions = [
    ...BASE_TARGETS,
    ...opts.field_definitions.map((f) => ({ value: `custom:${f.id}`, label: `Custom Field · ${f.label}` })),
  ];

  return (
    <Card className="p-5 mt-4 space-y-4" data-testid="pco-sync-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-[#2A2626]">Send to Planning Center</h3>
          <p className="text-sm text-[#5C5856]">When someone submits this card, send them to your church database.</p>
        </div>
        <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg((p) => ({ ...p, enabled: v }))} data-testid="pco-sync-enabled" />
      </div>

      {cfg.enabled && (
        <>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-[#5C5856]">Match each question to Planning Center</Label>
            {fields.map((f) => (
              <div key={f.id} className="flex items-center gap-3">
                <span className="flex-1 text-sm text-[#2A2626] truncate">{f.label}</span>
                <Select value={cfg.field_map[f.id] || NONE} onValueChange={(v) => setTarget(f.id, v)}>
                  <SelectTrigger className="h-9 w-56" data-testid={`pco-map-${f.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {targetOptions.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>

          {Object.values(cfg.field_map).includes("phone") && (
            <div className="flex items-center gap-3 pt-1" data-testid="pco-phone-location-row">
              <span className="flex-1 text-sm text-[#2A2626]">Phone Type <span className="text-[#8A857F]">(how the number is saved in Planning Center)</span></span>
              <Select value={cfg.phone_location || "Mobile"} onValueChange={(v) => setCfg((p) => ({ ...p, phone_location: v }))}>
                <SelectTrigger className="h-9 w-56" data-testid="pco-phone-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Mobile", "Home", "Work", "Other"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-[#E5E2DC]">
            <div>
              <Label className="text-sm">Add to Workflow</Label>
              <Select value={cfg.workflow_id || NONE} onValueChange={(v) => setCfg((p) => ({ ...p, workflow_id: v === NONE ? null : v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="pco-workflow"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {opts.workflows.map((w) => <SelectItem key={w.id} value={w.id}>{w.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Save Note As</Label>
              <Select value={cfg.note_category_id || NONE} onValueChange={(v) => setCfg((p) => ({ ...p, note_category_id: v === NONE ? null : v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="pco-note-category"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {opts.note_categories.map((n) => <SelectItem key={n.id} value={n.id}>{n.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}

      <Button onClick={save} disabled={saving} className="rounded-full bg-[#2A2626] hover:bg-[#1A1717] text-white" data-testid="pco-sync-save">
        {saving ? "Saving…" : "Save Planning Center settings"}
      </Button>
    </Card>
  );
}
